import { useState, type CSSProperties } from 'react'
import type { RouletteWinnerShareCard } from '@/lib/roulette'
import {
  buildCampaignBrandPalette,
  campaignBrandPaletteToStyle,
  resolveCampaignBrandColor,
} from '@/lib/campaignBrandPalette'
import { getRouletteShareCardCopy } from '@/lib/share-card/rouletteShareCardCopy'
import { SHARE_CARD_HEIGHT, SHARE_CARD_WIDTH } from '@/lib/share-card/domToPng'
import { RouletteWinnerShareWheelBackdrop } from './RouletteWinnerShareWheelBackdrop'
import { ShareCardDecorations } from './ShareCardDecorations'
import styles from './RouletteWinnerShareCard.module.css'

export interface RouletteWinnerShareCardProps {
  data: RouletteWinnerShareCard
}

function getCampaignInitials(title: string): string {
  const parts = title.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'CP'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

export function RouletteWinnerShareCard({ data }: RouletteWinnerShareCardProps) {
  const [logoFailed, setLogoFailed] = useState(false)
  const showLogo = Boolean(data.campaign.logo_url) && !logoFailed
  const campaignInitials = getCampaignInitials(data.campaign.title)
  const copy = getRouletteShareCardCopy(data.prize.prize_type)
  const isNonePrize = data.prize.prize_type === 'none'

  const palette = buildCampaignBrandPalette(
    resolveCampaignBrandColor(data.campaign.primary_color, data.campaign.button_color)
  )
  const brandStyle = campaignBrandPaletteToStyle(palette) as CSSProperties
  const rootStyle: CSSProperties = {
    ...brandStyle,
    width: SHARE_CARD_WIDTH,
    height: SHARE_CARD_HEIGHT,
  }

  return (
    <div className={styles.root} style={rootStyle} data-share-card-root>
      <ShareCardDecorations />

      <div className={styles.content}>
        <div className={styles.topSection}>
          <header className={styles.header}>
            <p className={styles.eyebrow}>Roleta da sorte</p>
            <p className={styles.subtitle}>Gire e concorra a prêmios da campanha</p>
          </header>

          <div className={styles.campaignBrand}>
            {showLogo ? (
              <img
                src={data.campaign.logo_url ?? ''}
                alt=""
                className={styles.campaignLogo}
                crossOrigin="anonymous"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className={styles.campaignLogoFallback} aria-hidden>
                {campaignInitials}
              </div>
            )}
            <p className={styles.campaignTitle}>{data.campaign.title}</p>
          </div>
        </div>

        <div className={styles.hero}>
          <RouletteWinnerShareWheelBackdrop
            roulette={data.roulette}
            primaryColor={data.campaign.primary_color}
            buttonColor={data.campaign.button_color}
          />

          <article
            className={`${styles.winnerCard} ${isNonePrize ? styles.winnerCardMuted : ''}`}
            style={brandStyle}
          >
            <p className={`${styles.congratsBadge} ${isNonePrize ? styles.congratsBadgeMuted : ''}`}>
              {copy.badge}
            </p>
            <h1 className={styles.congratsTitle}>{copy.title}</h1>
            <p className={styles.winnerName}>{data.winner.display_name}</p>
            {data.winner.display_phone ? (
              <p className={styles.winnerPhone}>{data.winner.display_phone}</p>
            ) : null}

            <div className={styles.prizeBlock}>
              <p className={styles.prizeLabel}>{copy.resultLabel}</p>
              <p
                className={
                  isNonePrize && data.prize.headline === '😞'
                    ? styles.prizeHeadlineEmoji
                    : styles.prizeHeadline
                }
              >
                {data.prize.headline}
              </p>
              {data.prize.detail ? <p className={styles.prizeDetail}>{data.prize.detail}</p> : null}
            </div>
          </article>
        </div>

        <div className={styles.bottomBand}>
          {showLogo ? (
            <img
              src={data.campaign.logo_url ?? ''}
              alt=""
              className={styles.bottomLogo}
              crossOrigin="anonymous"
              aria-hidden
            />
          ) : (
            <div className={styles.bottomLogoFallback} aria-hidden>
              {campaignInitials}
            </div>
          )}
          <p className={styles.campaignRepeat}>{data.campaign.title}</p>
          <p className={styles.cta}>Gire a roleta e concorra você também!</p>
          <footer className={styles.footer}>Roleta segura</footer>
        </div>
      </div>
    </div>
  )
}
